import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

export type ShippingDirection = 'import' | 'export';

interface DirectionContextValue {
  direction: ShippingDirection;
  setDirection: (d: ShippingDirection) => void;
  availableDirections: ShippingDirection[];
  hasBothDirections: boolean;
  isLoading: boolean;
}

const DirectionContext = createContext<DirectionContextValue | undefined>(undefined);

const STORAGE_KEY = 'tls.selectedDirection';

const DEFAULT_DIRECTIONS: ShippingDirection[] = ['import'];

function isShippingDirection(value: unknown): value is ShippingDirection {
  return value === 'import' || value === 'export';
}

function readStoredDirection(): ShippingDirection {
  if (typeof window === 'undefined') return 'import';
  const stored = localStorage.getItem(STORAGE_KEY);
  return isShippingDirection(stored) ? stored : 'import';
}

function writeStoredDirection(value: ShippingDirection) {
  localStorage.setItem(STORAGE_KEY, value);
}

export function DirectionProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: isAuthLoading } = useAuth();
  const [direction, setDirectionState] = useState<ShippingDirection>(() => readStoredDirection());

  // Direcțiile permise vin din contul autentificat; până se încarcă user-ul
  // portalul se comportă ca un cont pur-import (comportamentul istoric).
  const availableDirections = useMemo<ShippingDirection[]>(() => {
    const raw = user?.shippingDirections;
    if (!Array.isArray(raw)) return DEFAULT_DIRECTIONS;
    const filtered = raw.filter(isShippingDirection);
    return filtered.length > 0 ? filtered : DEFAULT_DIRECTIONS;
  }, [user]);

  useEffect(() => {
    writeStoredDirection(direction);
  }, [direction]);

  // Dacă direcția selectată nu mai e permisă pentru cont, revine la prima permisă
  useEffect(() => {
    if (availableDirections.includes(direction)) return;
    setDirectionState(availableDirections[0]);
  }, [availableDirections, direction]);

  const setDirection = useCallback((d: ShippingDirection) => {
    setDirectionState(d);
  }, []);

  const value = useMemo<DirectionContextValue>(
    () => ({
      direction,
      setDirection,
      availableDirections,
      hasBothDirections: availableDirections.length > 1,
      isLoading: !!isAuthLoading,
    }),
    [direction, setDirection, availableDirections, isAuthLoading],
  );

  return <DirectionContext.Provider value={value}>{children}</DirectionContext.Provider>;
}

export function useDirection() {
  const ctx = useContext(DirectionContext);
  if (!ctx) throw new Error('useDirection must be used within DirectionProvider');
  return ctx;
}
